/**
 * A published module, as a post.
 *
 * A module is two records — the post, and the `app.bsky.feed.module` record it
 * points at — and a feed carries only the first. Without this card a published
 * playlist appears in a timeline as a bare caption: the post says a course exists
 * and there is no way to see it, let alone play it.
 *
 * ## It is a playlist, not a post inside a post
 *
 * The first build of this card drew a *quoted post*: an avatar, a handle, a kind
 * chip, the title and the description, inside a bordered panel, under the post's
 * own avatar, handle and text. Every one of those was already on the screen twice,
 * and a reader scrolling past saw a post that had been shared by the person who
 * wrote it — which is exactly what a module is *not*: nobody shares a module, it
 * is written once and published (see `#/lib/modulePublish`).
 *
 * So the card shows only what the post does not already say:
 *
 *  - **No author row.** The post above it has one. Inside a direct message, where
 *    there is no post header, the module's maker is named by the message that
 *    carries it.
 *  - **The title and the description lead — even when the post says them.**
 *    Publishing copies the title into the post's own text, so the second build of
 *    this card drew it only when the post had none. That rule was written for a
 *    card whose top block was the *facts*: the title was furniture beside a
 *    sentence that already introduced the playlist, and printing both said one
 *    thing twice. Then the band was asked to lead with the course itself, and the
 *    rule stopped being right — a reader who has scrolled past the post is now met
 *    by the playlist's own name and what it is for, which is the one thing the
 *    post cannot tell them. A title repeated in a caption and again in a band is
 *    the price of that, and it is a cheap one.
 *  - **The band reads down.** What the course is called and what it is for, the
 *    strip of tiles, what it is and what it pays as tags under the strip, who has
 *    played it, and one primary action.
 *
 * ## It is a band across the post, not a card inside it
 *
 * The second build put all of that inside a panel — a shade off the page, a
 * hairline border, twelve points of padding on every side — and the panel was the
 * wrong idea three times over.
 *
 * The first reason is arithmetic. A feed row already opened with an avatar column,
 * so a card *inside* that column drew its frame with its own padding inside that,
 * and on a 390pt phone a playlist that could have had 358pt had 279: two nested
 * insets, the outer one there for a reason and the inner one there for none.
 *
 * The second reason is rhythm. A module is the post type the whole product is
 * built to popularise, and a timeline of them was becoming a stack of rounded
 * rectangles inside rounded rectangles — each one a frame that cost width the
 * playlist, the faces and the button all wanted back. The feed already has one
 * shape for *a thing that is not a post but travels with one*: the suggestion
 * section, which runs the full width of the row, wears the section surface
 * (`bg_contrast_25` — a shade off the page, in both themes), and is closed by a
 * rule. So that is what the card is now. It measures its offset inside the post
 * row the way the image gallery does (`GalleryBleed`, `measureLayout`) and draws
 * itself out to the row's edges; its content keeps the post's own gutter, so the
 * tags and the button still line up with the words above them. Surfaces with no
 * row to bleed into — a direct message's bubble — keep the column width and the
 * bubble's own tint, and skip the rule.
 *
 * The rule is only *below* the band. It used to run along the top as well, and it
 * read as a cut: a hairline drawn across the post's own words announced a new post
 * directly underneath, which is exactly what a module is not (see above). The tint
 * is what separates the band from the post; a line along one edge only is the
 * quietest way to end it.
 *
 * The tint is not decoration and it is not optional on light: a band painted in
 * the page's own colour is a white hole in a white timeline, which is exactly how
 * the previous build read. It is the same surface the *Suggested for you* carousel
 * and *Trending videos* sit on, which is what makes a module look like the other
 * rows the feed already knows how to draw.
 *
 * The third reason is the one the first two exist to serve: the card and the
 * module's own screen are one design. The module has a screen of its own — a big
 * title, the strip, how far this learner has got, and one full-width way in — and
 * a band that carries the same pieces says *the same thing* when it is opened.
 *
 * ## Everything painted *in* the band is told the band's own colour
 *
 * A carousel's edge fade is painted in the colour of whatever it is dissolving
 * into, and a face's ring is cut from the surface it stands on — and this component
 * is the only caller that knows either. Both, and the class block's own rings, take
 * the band's tint as a value (`fadeColor`, `surfaceColor`); painting them in the
 * page's colour would draw a stripe of the wrong shade across the strip and outline
 * every avatar in a colour nothing else on the card uses.
 *
 * ## Who is reading it decides the last line
 *
 * A learner is shown their classmates and offered the challenge; everybody else is
 * offered the way in. That decision is not made here — it is `#/lib/moduleAudience`
 * — but this is one of the two places it is drawn (see `ModuleAudience`). A learner
 * who is part way through a run is also shown how far they have got, and the button
 * says *Continue module* — the device's own record of the run (`#/state/moduleRuns`),
 * drawn on the card exactly as the module screen draws it.
 */
import {useCallback, useMemo, useRef, useState} from 'react'
import {Pressable, StyleSheet, View} from 'react-native'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {moduleCreatorOf, moduleRefOf} from '#/lib/modulePostCard'
import {isModulePost, moduleCardFacts} from '#/lib/modulePostCard'
import {readModulePreviewState} from '#/lib/moduleFixtures'
import {type NavigationProp} from '#/lib/routes/types'
import {useModuleByPostQuery, useModuleByUriQuery} from '#/state/queries/module'
import {useCurrentAccountProfile} from '#/state/queries/useCurrentAccountProfile'
import {atoms as a, useTheme} from '#/alf'
import {useGalleryBleed} from '#/components/images/Gallery'
import {Text} from '#/components/Typography'
import {ModuleAudience} from './ModuleAudience'
import {ModuleFactsTags} from './ModuleFactsTags'
import {ModulePlaylistCarousel} from './ModulePlaylistCarousel'
import {useModuleAudience} from './useModuleAudience'
import {useModulePreviewCase} from './useModulePreviewCase'
import {useModuleRunWithPreview} from './useModulePreviewRun'
import {useModuleTiles} from './useModuleTiles'
import {IS_DEV} from '#/env'

export function ModuleFeedEmbed({
  post,
  module: moduleField,
}: {
  post: {
    uri: string
    author: {did: string; handle: string; displayName?: string; avatar?: string}
    record: unknown
  }
  /** The module, when the feed's own response carries one. */
  module?: Record<string, unknown>
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  /*
   * The reader's own face, for the one case that is about them alone.
   *
   * The card of a module nobody has played, read by a learner of its level, draws
   * *them* in the class row (see `ModuleAudience`) — and the board cannot supply
   * that face, because a board is plays and they have not played. The profile is
   * the app's cached one, so a timeline of these asks for it once.
   */
  const profile = useCurrentAccountProfile()
  const viewerFace = useMemo(
    () => (profile ? {did: profile.did, avatar: profile.avatar} : undefined),
    [profile],
  )

  /**
   * The band's own surface — three things need it as a *value* rather than a
   * style: the strip's edge fades, which are painted in the colour they dissolve
   * into (see `fadeColor`), the faces' rings on the class row and on the door, and
   * the class's own block (`surfaceColor`), all of which are cut from it.
   *
   * It is the *section* surface rather than the page's, because that is what this
   * card is: the feed's own interstitials — *Suggested for you*, *Trending videos*
   * — are bands on `bg_contrast_25` between two hairlines, and a module that
   * travels inside a post is the same kind of object. Painted in the page's colour
   * it read as a white gap in the timeline on light and vanished into it on dark;
   * tinted, it reads as a section in both (see the header).
   */
  const band = t.atoms.bg_contrast_25.backgroundColor

  const postish = useMemo(
    () => ({record: post.record, module: moduleField}),
    [moduleField, post.record],
  )
  const ref = useMemo(() => moduleRefOf(postish), [postish])

  /*
   * Two ways to reach the record, and only one of them is ever taken: the post's
   * back-reference when the publisher managed to write it, and the author's own
   * records when it did not. Both are cached, so a feed scrolls past a hundred of
   * these without asking for the same playlist twice.
   */
  const {data: byUri} = useModuleByUriQuery(ref?.uri)
  const {data: byPost} = useModuleByPostQuery(
    ref ? '' : post.uri,
    ref ? '' : post.author.did,
  )
  const record = byUri ?? byPost ?? null

  const facts = useMemo(
    () => moduleCardFacts({post: postish, module: record}),
    [postish, record],
  )
  const {tiles} = useModuleTiles(facts.items)

  const moduleUri = facts.moduleUri
  const authorDid = useMemo(() => moduleCreatorOf(moduleUri), [moduleUri])

  /*
   * The test mode, read from the preview's own store rather than held here.
   *
   * It used to be a `useState` seeded once from storage, which meant a card could
   * only ever be switched by a press on *itself*: a switch on the module's page, or
   * the reel at the top of a post's header, left every card in the timeline drawing
   * the class it had first read — a preview whose switch does not visibly switch.
   * The store announces its writes (`#/components/modules/useModulePreviewCase`), so
   * a press anywhere redraws everywhere. `allCases` still comes back out of the
   * audience hook, so the chip and the facts behind the copy are the same question
   * asked once.
   */
  const {toggleCases, mode} = useModulePreviewCase()
  const {audience, allCases} = useModuleAudience({
    moduleUri: moduleUri ?? undefined,
    authorDid,
    level: facts.level,
    subject: facts.subject,
  })

  /*
   * How far *this* reader has got, on this device.
   *
   * A card in a timeline is drawn for whoever is holding the phone, and a learner
   * three capsules into a seven-capsule run should be told so — and offered
   * *Continue module* rather than *Play module* — where they meet it, not only after
   * they have opened it. The run is counted against the playlist as it stands, so a
   * capsule the maker removed cannot strand anybody.
   */
  const {progress} = useModuleRunWithPreview({
    moduleUri: moduleUri ?? undefined,
    items: facts.items,
  })

  /**
   * The band, measured out to the post row's edges.
   *
   * The row's own width arrives from the bleed context (`GalleryBleed`, which the
   * feed and the thread both mount); the band's own offset inside that row arrives
   * from `measureLayout`, and the two together are the insets the band pulls
   * itself out by — the same arithmetic the image gallery runs. A surface with no
   * bleed ancestor (a direct message's bubble) leaves `bleedWidth` at zero, and
   * the band stays a plain block on the bubble's own tint.
   *
   * ## What is measured is not what is moved
   *
   * The offset is read off an outer *slot* that stays exactly where the post's
   * gutter put it, and only the band *inside* it is pulled out by the insets.
   * Measuring the band itself looked equivalent and is not: the band is widened by
   * the measurement, the widening fires `onLayout` again, and the second reading —
   * taken from the band already pulled out to the row's edge — is zero. The insets
   * collapsed to nothing on the second pass, and the band settled sixteen points to
   * the right of the row, its right edge clipped by the feed. RNW's `measureLayout`
   * defers through a `setTimeout`, so this is one frame of layout, not a race. The
   * slot is never resized, so the reading is stable and the arithmetic converges on
   * the first pass. The image gallery splits the two for the same reason.
   */
  const {bleedRef, bleedWidth} = useGalleryBleed()
  const slotRef = useRef<View>(null)
  const [insets, setInsets] = useState<{left: number; right: number} | null>(
    null,
  )
  const measure = useCallback(() => {
    if (!slotRef.current || !bleedRef.current) return
    slotRef.current.measureLayout(
      bleedRef.current,
      (x, _y, width) => {
        setInsets({
          left: x,
          right: Math.max(0, bleedWidth - x - width),
        })
      },
      () => {},
    )
  }, [bleedRef, bleedWidth])
  const bled = bleedWidth > 0 && insets !== null

  const onOpen = useMemo(() => {
    if (!moduleUri) return undefined
    return () => navigation.navigate('Module', {moduleUri})
  }, [moduleUri, navigation])

  /*
   * The way to the results — which the class's faces *are*, for a learner (see
   * `ModuleAudience`).
   *
   * The invented class travels with the address, in a dev build only: the
   * leaderboard reads its board from its own route, so a card showing ninety-six
   * players and a board showing the class's real plays would be two answers to one
   * question. Outside `IS_DEV` the param is not sent at all.
   */
  const onOpenResults = useMemo(() => {
    if (!moduleUri) return undefined
    return () =>
      navigation.navigate('ModuleLeaderboard', {
        moduleUri,
        preview: IS_DEV ? (readModulePreviewState() ?? undefined) : undefined,
      })
  }, [moduleUri, navigation])

  return (
    <View
      testID="moduleFeedEmbed"
      ref={slotRef}
      onLayout={measure}
      style={a.w_full}>
      <View
        style={[
          a.w_full,
          a.gap_md,
          bled && insets
            ? [
                styles.band,
                t.atoms.bg_contrast_25,
                {
                  marginLeft: -insets.left,
                  width: bleedWidth,
                  paddingLeft: insets.left,
                  paddingRight: insets.right,
                  borderBottomColor: t.atoms.border_contrast_low.borderColor,
                },
              ]
            : styles.noBleed,
        ]}>
        {/*
          What the course is called, and what it is for.

          The title leads every card now, whether or not the post above it already
          used the words (see the header), and the description comes with it — a
          title is a name and a description is the reason to press. Both are one
          press target, because they are one thing.
        */}
        <Pressable
          testID="moduleFeedEmbedOpen"
          accessibilityRole="button"
          accessibilityLabel={l`Open the module ${facts.title}`}
          accessibilityHint={l`Opens the playlist of capsules`}
          disabled={!onOpen}
          onPress={onOpen}
          style={({pressed}) => [a.gap_xs, pressed && styles.pressed]}>
          <Text
            testID="moduleFeedEmbedTitle"
            numberOfLines={2}
            style={[a.text_lg, a.font_bold, a.leading_tight, t.atoms.text]}>
            {facts.title}
          </Text>
          {facts.description ? (
            <Text
              testID="moduleFeedEmbedDescription"
              numberOfLines={2}
              style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
              {facts.description}
            </Text>
          ) : null}
        </Pressable>

        {tiles.length > 0 ? (
          <ModulePlaylistCarousel
            tiles={tiles}
            testID="moduleFeedEmbedPlaylist"
            tileTestIDPrefix="moduleFeedTile"
            /*
            No rail under the strip here.

            On a card the line of segments read as *progress*, directly above the
            line that says the progress in words (*You have finished 1 of 6
            capsules*) — and it is not progress: it is which tile the strip is
            scrolled to. The card keeps the words and drops the bar (see `showRail`,
            and the module's own page, where the rail is a scroll affordance and
            stays).
          */
            showRail={false}
            fadeColor={band}
            accessibilityHint={l`Opens the module at that capsule`}
            onPressTile={
              onOpen
                ? tile =>
                    navigation.navigate('ModulePlay', {
                      moduleUri: moduleUri!,
                      start: tile.position - 1,
                    })
                : undefined
            }
          />
        ) : null}

        {/*
        What the module is: which subject, which year, and what it pays. The tags
        keep the `testID` the sentence they replaced had, because they are the same
        slot saying the same facts — just under the strip rather than over it, where
        a reader has counted the capsules and wants the two things the tiles cannot
        tell them.
      */}
        <ModuleFactsTags
          testID="moduleFeedEmbedFacts"
          level={facts.level}
          subject={facts.subject}
          coins={facts.coins}
        />

        {/*
        Who is reading decides this block, and it ends in the one button.

        `testID`s keep the names the first build's card used, so anything already
        driving this card — a recipe, a spec, the preview — still finds it.
      */}
        <ModuleAudience
          testID="moduleFeedEmbedAudience"
          audience={audience}
          title={facts.title}
          viewerFace={viewerFace}
          progress={progress}
          surfaceColor={band}
          allCases={allCases}
          /*
           * The sheet's door, offered only while the preview is on: a chip that
           * opened a sheet of invented cases inside a *real mode* card would be the
           * one piece of the tool that survived the switch (`useModulePreviewCase`).
           */
          onToggleCases={mode ? toggleCases : undefined}
          onOpenBoard={onOpenResults}
          onPlay={() =>
            moduleUri && navigation.navigate('ModulePlay', {moduleUri})
          }
        />
      </View>
    </View>
  )
}

/**
 * Whether a post should be drawn as a module.
 *
 * Exported so the four places that render a post — a feed row, a thread's anchor,
 * its replies, and a direct message — ask the question in exactly one way, which
 * is the rule the book's card follows too. A post is a module if it says so, and
 * the reverse lookup inside the card covers the case where neither marker
 * survived, so a false answer here costs nothing but a playlist that is only a
 * caption.
 */
export function hasModuleEmbed(post: {
  record?: unknown
  module?: unknown
}): boolean {
  return isModulePost(post)
}

const styles = StyleSheet.create({
  /*
   * The band: the row's own width, on the section surface (`bg_contrast_25`, added
   * at the call site), closed by a rule along its *bottom* only, with the content
   * inside it pulled back to the post's own gutter (the measured insets). There is
   * no padding of the card's own left or right — the gutter *is* the card's margin
   * — and no corner to round.
   *
   * The rule used to run along the top as well, and it read as a cut: a hairline
   * across the post's own words announced a *new post* rather than a quieter
   * section of this one. The tint is what separates the two (see the call site);
   * the rule below is what ends the band, and it is the only line the card needs.
   */
  band: {
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  /*
   * The band without a row to bleed into — a direct message's bubble — keeps the
   * column width and the bubble's own tint, and skips the rules.
   */
  noBleed: {
    paddingVertical: 2,
  },
  pressed: {
    opacity: 0.95,
  },
})
